/**
 * Rastreio e previsão sobre o mapa: o botão flutuante e o painel pequeno,
 * dedicado só a isso.
 *
 * - Desktop: aba na borda direita do mapa, acima da dos "Últimos focos"; abre
 *   um cartão flutuante (não encaixado: o mapa não muda de tamanho) com duas
 *   abas (desde 03/11/2026): Nuvens — os três controles (Rastreio, Previsão,
 *   Núcleos internos), os números do quadro, a antecedência da previsão e a
 *   lista dos sistemas, recolhida até ser pedida — e Chuva, o rastreio das
 *   áreas de chuva do GSMaP (`rastreioChuva/PainelDaChuva`). Os dois rastreios
 *   podem ficar no mapa ao mesmo tempo; cada aba diz se o dela está.
 * - Celular: botão abaixo do dos últimos focos; abre uma folha inferior com o
 *   mesmo conteúdo.
 * - `ForecastChip`: o aviso sobre o mapa enquanto há polígono previsto na tela,
 *   com a moldura ciano em volta do mapa e o botão "Agora".
 * - `PyFortraccMark`: a assinatura do algoritmo no canto inferior direito (no celular, no esquerdo).
 */
import clsx from 'clsx';
import { ArrowUp, ChevronDown, Cloud, Droplets, History, Pause, Play, X } from 'lucide-react';
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type OlMap from 'ol/Map';
import { formatInt } from '../lib/format';
import { formatTimeBrt, MINUTE_MS } from '../lib/time';
import { useMapInstance } from '../map/mapContext';
import { openPopupAt } from '../map/popupControl';
import { painelSobreOMapa, SIDEBAR_WIDTH } from '../map/viewControl';
import { useUiStore } from '../state/ui';
import { useGoesStore } from '../state/goes';
import { shownTimeOf, useTimeStore } from '../state/time';
import { useNow, usePainelLateralAberto } from '../ui/hooks';
import { useModalLayer } from '../ui/modalStack';
import { IconButton } from '../ui/primitives';
import { BASE_RASTREIO, PYFORTRACC_URL } from './api';
import { IconeRastreio } from './icone';
import { areaDoSistema } from './layer';
import { chaveDoSistema } from './registro';
import { ALFA_DAS_FAIXAS_DO_CONE, comAlfa, COR_PREVISAO, COR_SELECAO, COR_SISTEMA, TRACO_CHUVA, TRACO_NUCLEO, TRACO_PREVISTO, TRACO_SISTEMA } from './tracos';
import { Abas, Aviso, Interruptor, Numeros, Traco } from './controles';
import { BASE_RASTREIO_CHUVA } from '../rastreioChuva/api';
import { areaCurta, irAChuva, PainelDaChuva } from '../rastreioChuva/PainelDaChuva';
import { useRastreioChuvaStore, type SistemaChuvaNaLista } from '../rastreioChuva/store';
import { antecedenciaEscolhida, antecedenciaNaTela, previsaoDoSelecionado, previsaoVazia, useTrackingStore, type AbaDoRastreio, type EstadoNoQuadro, type SistemaNaLista } from './store';
import { sairDaPrevisao, verPrevisao } from './fluxo';
import { usePrevisaoDesenhada } from './ZonaDaPrevisao';
import { antecedenciaPorExtenso, antecedenciasNoQuadro, avisoDeAntecedencia, motivoDaFalta, rotuloAntecedencia } from './tempo';
import { carregarRastreio, refreshRastreio, ultimoQuadro } from './sync';
import { avisoDeFrescor, quadroMaisNovoComPrevisao, quadroParaInstante } from './tempo';
import type { QuadroRastreio } from './api';
import { pontoCardeal, TrackingPopup } from './TrackingPopup';

/** Sistemas na lista antes de "ver todos". */
const LISTA_CURTA = 6;

/** Frase do rastreio no quadro exibido. */
export function fraseDoRastreio(q: QuadroRastreio | null, estado: EstadoNoQuadro, ultimo: number | null, instante: number | null): string {
  if (instante === null) return '—';
  if (!q) {
    return ultimo !== null && instante > ultimo
      ? `O rastreio deste horário ainda não chegou (o mais novo é das ${formatTimeBrt(ultimo)}): o mapa mostra só a imagem.`
      : 'Sem rastreio deste horário: o mapa mostra só a imagem.';
  }
  if (estado === 'carregando') return 'Baixando os contornos deste horário…';
  if (estado === 'falhou') return 'Os contornos deste horário não carregaram.';
  if (q.n === 0) return 'Nenhum sistema rastreado neste horário.';
  const sistemas = q.n === 1 ? '1 sistema' : `${formatInt(q.n)} sistemas`;
  return `${sistemas}, ${formatInt(q.km2)} km² no total${q.tmin !== null ? `; topo mais frio a ${formatInt(q.tmin)} K` : ''}.`;
}

/** Frase da previsão no quadro exibido. `vazia`: o arquivo chegou sem nenhum contorno. */
export function fraseDaPrevisao(q: QuadroRastreio | null, estado: EstadoNoQuadro, antecedencia: number, vazia = false): string {
  if (!q) return 'Sem rastreio deste horário, então sem previsão: o mapa mostra só a imagem.';
  if (!q.prev) return 'Sem previsão a partir deste horário: o mapa mostra só a imagem.';
  if (estado === 'carregando') return 'Baixando a previsão deste horário…';
  if (estado === 'falhou') return 'A previsão deste horário não carregou: o mapa mostra só a observação.';
  if (vazia) return 'Nenhum sistema previsto a partir deste horário: não há contorno previsto para desenhar.';
  if (antecedencia <= 0) return `Observação das ${formatTimeBrt(q.t)}: a previsão parte daqui.`;
  return `Onde os sistemas das ${formatTimeBrt(q.t)} devem estar às ${formatTimeBrt(q.t + antecedencia * MINUTE_MS)}.`;
}

/** Leva o mapa até o sistema e abre a ficha dele. */
function irAoSistema(map: OlMap, item: SistemaNaLista, mobile: boolean): void {
  const view = map.getView();
  view.cancelAnimations();
  view.fit(item.extensao, {
    // À direita, o cartão flutuante (desktop); embaixo, a barra do celular. No desktop a ficha
    // abre AO LADO do sistema, sem cobri-lo: a folga da esquerda é o lugar dela (largura da
    // ficha, 18rem mais as margens).
    padding: mobile ? [80, 24, 96, 24] : [96, 420, 64, 360 + painelSobreOMapa()],
    duration: 650,
    // Um sistema pequeno não leva o mapa ao zoom máximo: o entorno importa.
    maxZoom: 7,
    // Só abre se o voo terminou (o usuário pode interrompê-lo arrastando o mapa).
    callback: (completo) => {
      if (completo) openPopupAt(item.ponto, <TrackingPopup sistema={item.sistema} />, { movel: true, evitar: areaDoSistema(item.sistema) ?? undefined });
    },
  });
}

/** Nome da opção dos núcleos, o mesmo em toda a interface. */
export const NOME_NUCLEOS = 'Núcleos internos';

// As descrições são as mesmas no painel flutuante e na aba Satélite.

export const DESCRICAO_DO_RASTREIO = 'Contorno dos sistemas de nuvens frias na imagem exibida.';

/**
 * A descrição da previsão, com o alcance (a maior antecedência do índice, em
 * min) quando se sabe. Diz onde mais ela está: na linha do tempo, à direita.
 * Curta: o interruptor tem a altura do do Rastreio (duas linhas).
 */
export function descricaoDaPrevisao(maior: number | undefined): string {
  return `Cada sistema ${maior !== undefined ? `até ${antecedenciaPorExtenso(maior)} à frente` : 'nas próximas horas'}. Também na linha do tempo.`;
}

/**
 * O interruptor "Previsão" liga pela mesma porta da zona da linha do tempo e
 * do botão da barra (`verPrevisao`): pula para a ÚLTIMA imagem rastreada e
 * mostra só a previsão dela (pedido do usuário, 08/10/2026). Sem nenhum quadro
 * com previsão ela liga assim mesmo: a linha de situação diz por que não há nada.
 */
export function ligarPrevisao(on: boolean): void {
  if (!on) useTrackingStore.getState().setPrevisao(false);
  else if (!verPrevisao(undefined, 'mais-nova')) useTrackingStore.getState().setPrevisao(true);
}

/** A descrição dos núcleos, com os limiares do índice quando ele os traz. */
export function descricaoDosNucleos(limiares: readonly number[] | undefined): string {
  const internos = (limiares ?? []).slice(1);
  return internos.length
    ? `Topos mais frios que ${internos.map((l) => formatInt(l)).join(' e ')} K.`
    : 'Os topos mais frios de cada sistema.';
}

/** A descrição dos núcleos sem rastreio nem previsão no mapa: diz quando eles aparecem. */
export function descricaoDosNucleosSemEfeito(): string {
  // Uma linha só, como a descrição normal: a linha dos núcleos tem a mesma altura nos dois
  // estados. Se a preferência está guardada, quem diz é o estado escrito ao lado ("Guardado").
  return 'Só com Rastreio ou Previsão.';
}

/**
 * "Núcleos internos": uma opção DO que está no mapa, não uma terceira camada.
 * Linha secundária, recuada sob Rastreio e Previsão e presa a eles por um fio:
 * sem cartão, texto menor e de menos peso, interruptor pequeno. O primeiro
 * desenho (29/09/2026) a fez igual aos dois principais, e passou do ponto: a
 * ênfase é do rastreio e da previsão. Continua à vista e a um toque — a área
 * de toque (44 px no celular) é maior que o desenho.
 *
 * Sem rastreio nem previsão no mapa a opção não tem efeito: fica neutra (nada
 * de verde), diz "Guardado" no lugar de "Ligado" e explica quando aparece —
 * mas continua operável. É o MESMO componente no painel flutuante e na aba
 * Satélite: os dois mostram o mesmo estado.
 */
export function InterruptorDosNucleos() {
  const id = useId();
  const ligado = useTrackingStore((s) => s.nucleos);
  const semEfeito = useTrackingStore((s) => !s.rastreio && !s.previsao);
  const limiares = useTrackingStore((s) => s.indice?.limiares);
  const aceso = ligado && !semEfeito;
  return (
    // O fio à esquerda e o recuo dizem de quem a opção é. O vão de cima é o mesmo que separa os
    // dois principais (6 px, do `space-y` de quem a contém).
    <div className="ml-3.5 border-l border-ink-600 pl-2.5">
      <button
        type="button"
        role="switch"
        aria-checked={ligado}
        aria-label={NOME_NUCLEOS}
        aria-describedby={id}
        onClick={() => useTrackingStore.getState().setNucleos(!ligado)}
        className="flex min-h-9 w-full items-center gap-2 rounded-md py-1 pr-1 pl-1 text-left hover:bg-ink-800/70 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-mata-400 pointer-coarse:min-h-11"
      >
        <Traco traco={TRACO_NUCLEO} apagado={!aceso} curto />
        <span className="min-w-0 flex-1 leading-tight">
          <span className={clsx('block text-xs', aceso ? 'text-ink-100' : 'text-ink-200')}>{NOME_NUCLEOS}</span>
          <span id={id} className="block truncate text-[11px] leading-snug text-ink-300">
            {semEfeito ? descricaoDosNucleosSemEfeito() : descricaoDosNucleos(limiares)}
          </span>
        </span>
        <span className="flex shrink-0 items-center gap-1.5" aria-hidden>
          <span className={clsx('text-[11px] leading-none', aceso ? 'font-semibold text-mata-300' : 'text-ink-400')}>
            {!ligado ? 'Desligado' : semEfeito ? 'Guardado' : 'Ligado'}
          </span>
          <span className={clsx('relative inline-flex h-4 w-7 items-center rounded-full transition-colors', aceso ? 'bg-mata-500' : 'bg-ink-600')}>
            <span
              className={clsx(
                'inline-block size-3 rounded-full shadow transition-transform',
                semEfeito ? 'bg-ink-400' : 'bg-white',
                ligado ? 'translate-x-3.5' : 'translate-x-0.5',
              )}
            />
          </span>
        </span>
      </button>
    </div>
  );
}

/**
 * A linha do tempo da previsão: a observação ("agora") e as antecedências, com
 * o botão que toca o fluxo. É um grupo de opções: uma parada de Tab só, e as
 * setas movem a escolha, pulando as que o quadro não tem.
 */
function Antecedencias(props: {
  q: QuadroRastreio | null;
  passos: readonly number[];
  disponiveis: readonly number[];
  naTela: number;
  /** O quadro tem previsão para tocar. */
  ativa: boolean;
  /** Há contorno previsto na tela: só então o horário válido faz sentido. */
  desenhada: boolean;
  /** Tocaram numa antecedência que o quadro não tem. */
  aoFaltar: (min: number) => void;
  /** Por que a antecedência tocada não existe, ou null. Fica colado ao seletor: é a resposta ao toque. */
  motivo: string | null;
}) {
  const { q, passos, disponiveis, naTela, ativa, desenhada } = props;
  const fluxo = useTrackingStore((s) => s.fluxo);
  const { setAntecedencia, setFluxo } = useTrackingStore.getState();
  const tocando = fluxo && ativa;
  const grupo = useRef<HTMLDivElement>(null);
  const recado = useRef<HTMLParagraphElement>(null);
  // Em tela baixa o motivo pode nascer abaixo da dobra: vem para a parte visível.
  useEffect(() => {
    if (props.motivo) recado.current?.scrollIntoView({ block: 'nearest' });
  }, [props.motivo]);
  // Sem o "0" (só a observação): desnecessário, pediu o usuário em 08/10/2026 — sair da previsão é
  // desligá-la ou o "Agora" do aviso; o fluxo continua passando pela observação.
  const opcoes = passos;
  const escolhiveis = opcoes.filter((a) => disponiveis.includes(a));
  // Na observação (passo 0 do fluxo) nenhum está marcado: o primeiro recebe o foco do teclado.
  const focavel = escolhiveis.includes(naTela) ? naTela : escolhiveis[0];

  const comSetas = (e: React.KeyboardEvent) => {
    const i = Math.max(0, escolhiveis.indexOf(naTela));
    let alvo: number | undefined;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') alvo = escolhiveis[(i + 1) % escolhiveis.length];
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') alvo = escolhiveis[(i - 1 + escolhiveis.length) % escolhiveis.length];
    else if (e.key === 'Home') alvo = escolhiveis[0];
    else if (e.key === 'End') alvo = escolhiveis[escolhiveis.length - 1];
    if (alvo === undefined) return;
    e.preventDefault();
    setAntecedencia(alvo);
    grupo.current?.querySelector<HTMLElement>(`[data-antecedencia="${alvo}"]`)?.focus();
  };

  return (
    <div className="space-y-1">
      {/* O botão do fluxo fica na linha do título: os 7 botões precisam da largura toda (47 px cada em 360 px). */}
      <div className="flex items-center gap-2 text-[11px] text-ink-300">
        <div className="min-w-0 flex-1 leading-snug">
          <div>Antecedência{tocando ? ' · fluxo tocando' : ''}</div>
          {q?.prev && (
            <div className="tabular-nums" data-testid="previsao-valida">
              imagem {formatTimeBrt(q.t)}
              {desenhada && naTela > 0 && (
                <>
                  {' '}
                  → <strong className="font-semibold text-white">{formatTimeBrt(q.t + naTela * MINUTE_MS)}</strong>
                </>
              )}
            </div>
          )}
        </div>
        <IconButton
          icon={tocando ? Pause : Play}
          size="sm"
          label={tocando ? 'Pausar o fluxo da previsão' : 'Tocar o fluxo da previsão'}
          active={tocando}
          disabled={!ativa}
          onClick={() => setFluxo(!tocando)}
          className="shrink-0 pointer-coarse:size-11"
        />
      </div>
      <div
        ref={grupo}
        role="radiogroup"
        aria-label="Antecedência da previsão"
        onKeyDown={comSetas}
        className="flex rounded-lg border border-ink-600 bg-ink-800 p-0.5"
      >
        {opcoes.map((a) => {
          const falta = a > 0 && !disponiveis.includes(a);
          return (
            <button
              key={a}
              type="button"
              role="radio"
              data-antecedencia={a}
              aria-checked={a === naTela}
              // `aria-disabled`, não `disabled`: o toque tem de responder, dizendo por que não há.
              aria-disabled={falta || undefined}
              aria-label={`+${antecedenciaPorExtenso(a)}${falta ? ' (esta imagem não tem)' : ''}`}
              tabIndex={a === focavel ? 0 : -1}
              onClick={() => (falta ? props.aoFaltar(a) : setAntecedencia(a))}
              className={clsx(
                'min-h-8 min-w-0 flex-1 rounded-md text-[11px] font-medium whitespace-nowrap tabular-nums transition-colors pointer-coarse:min-h-11',
                'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-mata-400',
                // Riscada e mais apagada que as disponíveis, mas legível (5:1 sobre o fundo).
                a === naTela ? 'bg-ink-600 text-white shadow-sm' : falta ? 'text-ink-400 line-through' : 'text-ink-200 hover:text-white',
              )}
            >
              {rotuloAntecedencia(a)}
            </button>
          );
        })}
      </div>
      {props.motivo && (
        <p ref={recado} role="status" data-testid="motivo-da-falta" className="scroll-mb-2 px-0.5 text-[11px] leading-snug text-yellow-200">
          {props.motivo}
        </p>
      )}
    </div>
  );
}

function Sistemas(props: { itens: readonly SistemaNaLista[]; onPick: (item: SistemaNaLista) => void }) {
  const aberta = useTrackingStore((s) => s.listaAberta);
  const todos = useTrackingStore((s) => s.listaInteira);
  const selecionada = useTrackingStore((s) => s.selecao?.chave ?? null);
  const { setListaAberta, setListaInteira } = useTrackingStore.getState();
  const id = useId();
  const caixa = useRef<HTMLDivElement>(null);
  const pedida = useRef(false);
  // Aberta pelo usuário, a lista sobe para o topo da área rolável: os itens nasceriam abaixo da dobra.
  useEffect(() => {
    if (!aberta || !pedida.current) return;
    pedida.current = false;
    const calmo = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    caixa.current?.scrollIntoView({ block: 'start', behavior: calmo ? 'auto' : 'smooth' });
  }, [aberta]);
  // Ao abrir o painel com a lista aberta e um sistema selecionado, o item dele vem para a parte
  // visível (estaria abaixo da dobra). Se está além dos maiores, a lista se mostra inteira.
  const posicao = selecionada === null ? -1 : props.itens.findIndex((i) => i.sistema.uid !== null && chaveDoSistema(i.sistema) === selecionada);
  const montou = useRef(false);
  useEffect(() => {
    if (montou.current || !aberta || posicao < 0) return;
    if (posicao >= LISTA_CURTA && !todos) {
      setListaInteira(true);
      return;
    }
    montou.current = true;
    const calmo = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    caixa.current?.querySelector('[aria-current="true"]')?.scrollIntoView({ block: 'center', behavior: calmo ? 'auto' : 'smooth' });
  }, [aberta, posicao, todos, setListaInteira]);
  const itens = todos ? props.itens : props.itens.slice(0, LISTA_CURTA);
  if (!props.itens.length) return null;
  return (
    <div ref={caixa} data-lista-de-sistemas className="scroll-mt-2 overflow-hidden rounded-lg border border-ink-700/70">
      <button
        type="button"
        aria-expanded={aberta}
        aria-controls={id}
        onClick={() => {
          pedida.current = !aberta;
          setListaAberta(!aberta);
        }}
        className="flex min-h-10 w-full items-center gap-2 bg-ink-800/60 px-2.5 text-left text-xs font-medium text-ink-100 hover:bg-ink-700/60 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-mata-400 pointer-coarse:min-h-12"
      >
        <span className="min-w-0 flex-1 truncate">
          Sistemas nesta imagem <span className="text-ink-400">·</span> <span className="tabular-nums">{formatInt(props.itens.length)}</span>
        </span>
        <span className="shrink-0 text-[11px] font-normal text-ink-300">{aberta ? 'Recolher' : 'Ver a lista'}</span>
        <ChevronDown className={clsx('size-4 shrink-0 text-ink-300 transition-transform', aberta && 'rotate-180')} aria-hidden />
      </button>
      <div id={id} hidden={!aberta}>
        {aberta && (
          <>
            <p className="border-t border-ink-700/60 px-2.5 py-1 text-[11px] text-ink-300">Do maior para o menor. Toque num sistema para ir até ele.</p>
            <ul className="divide-y divide-ink-700/60 border-t border-ink-700/60">
              {itens.map(({ sistema: s, ponto, extensao }) => {
                // O da ficha aberta: o mesmo que está destacado no mapa.
                const atual = s.uid !== null && chaveDoSistema(s) === selecionada;
                return (
                <li key={`${String(s.uid)}-${String(s.lim)}`}>
                  <button
                    type="button"
                    aria-current={atual ? 'true' : undefined}
                    onClick={() => props.onPick({ sistema: s, ponto, extensao })}
                    className={clsx(
                      'flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-xs hover:bg-ink-700/60 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-mata-400 pointer-coarse:min-h-11',
                      // Barra à esquerda na cor do destaque no mapa (violeta), e fundo mais claro.
                      atual ? 'bg-ink-700/80' : 'bg-ink-800/40',
                    )}
                    style={atual ? { boxShadow: `inset 3px 0 0 ${COR_SELECAO}` } : undefined}
                  >
                    <span className="w-8 shrink-0 font-semibold text-white tabular-nums">{s.uid ?? '—'}</span>
                    <span className="min-w-0 flex-1 truncate text-ink-200 tabular-nums">
                      {s.km2 !== null ? `${formatInt(s.km2)} km²` : '—'}
                      {s.tmin !== null && <span className="text-ink-400"> · {formatInt(s.tmin)} K</span>}
                    </span>
                    {s.vel !== null && (
                      <span className="flex shrink-0 items-center gap-1 text-ink-200 tabular-nums" title={s.rumo !== null ? `para ${pontoCardeal(s.rumo)}` : undefined}>
                        {formatInt(s.vel)} km/h
                        {s.rumo !== null && s.vel > 0 && <ArrowUp className="size-3.5 text-mata-300" style={{ transform: `rotate(${s.rumo}deg)` }} aria-hidden />}
                      </span>
                    )}
                    {atual && <span className="sr-only">(selecionado)</span>}
                  </button>
                </li>
                );
              })}
            </ul>
            {props.itens.length > LISTA_CURTA && (
              <button
                type="button"
                onClick={() => setListaInteira(!todos)}
                className="flex min-h-9 w-full items-center border-t border-ink-700/60 px-2.5 text-[11px] font-medium text-mata-300 hover:underline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-mata-400 pointer-coarse:min-h-11"
              >
                {todos ? 'Ver só os maiores' : `Ver os ${props.itens.length} sistemas`}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/**
 * O conteúdo do painel: o mesmo no cartão do desktop e na folha do celular. A
 * área rolável que o contém reserva o espaço da barra de rolagem
 * (`scrollbar-gutter`): sem isso a barra, ao aparecer, estreita o conteúdo, o
 * texto requebra e os controles mudam de altura sob o dedo. No desktop o
 * conteúdo tem largura fixa e a reserva fica por FORA dela (o cartão alarga):
 * a largura útil não paga pela barra.
 */
export function TrackingBody(props: { onPick: (item: SistemaNaLista) => void }) {
  const rastreio = useTrackingStore((s) => s.rastreio);
  const previsao = useTrackingStore((s) => s.previsao);
  const naTela = useTrackingStore((s) => antecedenciaNaTela(s));
  const situacao = useTrackingStore((s) => s.situacao);
  const indice = useTrackingStore((s) => s.indice);
  const noQuadro = useTrackingStore((s) => s.noQuadro);
  const sistemas = useTrackingStore((s) => s.sistemas);
  const escolhida = useTrackingStore((s) => antecedenciaEscolhida(s));
  const fluxo = useTrackingStore((s) => s.fluxo);
  const hNoQuadro = useTrackingStore((s) => s.hNoQuadro);
  const vazia = useTrackingStore((s) => previsaoVazia(s));
  const [faltou, setFaltou] = useState<{ min: number; c: string | undefined } | null>(null);
  const { setRastreio } = useTrackingStore.getState();
  const satelite = useGoesStore((s) => s.layers.some((l) => l.visible));
  const instante = useTimeStore((s) => shownTimeOf(s));
  const nucleos = useTrackingStore((s) => s.nucleos);
  const now = useNow(60_000);
  const naOrigem = useTrackingStore((s) => s.naOrigem);
  const frames = useTimeStore((s) => s.frames);

  const quadros = indice?.quadros ?? [];
  const q = quadroParaInstante(
    quadros,
    quadros.map((x) => x.t),
    instante,
  );
  const ultimo = ultimoQuadro(indice);
  const { passos, disponiveis } = antecedenciasNoQuadro(indice?.antecedencias ?? [], noQuadro.previsao === 'ok' ? hNoQuadro : null);
  const ligada = rastreio || previsao;
  const maior = indice?.antecedencias[indice.antecedencias.length - 1];
  // Há contorno previsto para desenhar neste quadro.
  const prevista = !!q?.prev && noQuadro.previsao === 'ok' && !vazia;
  // Previsão ligada numa imagem anterior à origem (a mais nova do loop com previsão): nada previsto
  // para todos aqui — a linha diz por quê e leva à previsão.
  const foraDaOrigem = previsao && !naOrigem && !!indice && quadroMaisNovoComPrevisao(frames, indice.quadros) >= 0;
  // Com o rastreio ligado a linha de situação fala dele; a previsão entra quando tem algo a dizer.
  const falarDaPrevisao = previsao && (foraDaOrigem || (!!q && (!rastreio || !prevista)));
  const motivo = faltou && faltou.c === q?.c && !disponiveis.includes(faltou.min) ? motivoDaFalta(faltou.min, disponiveis) : null;
  // Com o fluxo tocando a antecedência anda sozinha: o aviso é para a que o usuário fixou.
  const semEscolhida = previsao && !fluxo && noQuadro.previsao === 'ok' ? avisoDeAntecedencia(escolhida, naTela, disponiveis) : null;
  // A imagem mais nova costuma chegar antes do rastreio dela: um toque leva ao último horário rastreado.
  const indiceDoUltimo = useTimeStore((s) => (ultimo !== null ? s.frames.indexOf(ultimo) : -1));
  const irAoUltimo = !q && indiceDoUltimo >= 0 && instante !== null && ultimo !== null && instante > ultimo;
  const aviso = ligada ? avisoDeFrescor(ultimo, now) : null;
  // Com os contornos na tela, os números do quadro vão para os quadros grandes; a caixa fica para o resto.
  const numerosOk = rastreio && !!q && noQuadro.rastreio === 'ok' && q.n > 0;
  const fraseNaCaixa = !numerosOk && (rastreio || !q);

  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <Interruptor nome="Rastreio" descricao={DESCRICAO_DO_RASTREIO} ligado={rastreio} traco={TRACO_SISTEMA} onChange={setRastreio} />
        <Interruptor nome="Previsão" descricao={descricaoDaPrevisao(maior)} ligado={previsao} traco={TRACO_PREVISTO} onChange={ligarPrevisao} />
        <InterruptorDosNucleos />
      </div>

      {ligada && !satelite && <Aviso>As imagens do satélite estão desligadas: o rastreio e a previsão aparecem junto com elas.</Aviso>}
      {aviso && <Aviso>{aviso}</Aviso>}
      {ligada && situacao.estado === 'error' && !indice && (
        <Aviso>
          Não deu para baixar a lista de quadros do rastreio: {situacao.erro}{' '}
          <button type="button" className="font-semibold underline" onClick={() => void carregarRastreio(true)}>
            Tentar de novo
          </button>
        </Aviso>
      )}
      {ligada && situacao.estado === 'loading' && !indice && <p className="text-xs text-ink-300">Baixando a lista de quadros…</p>}

      {ligada && indice && satelite && (
        <>
          {previsao && passos.length > 0 && (
            <Antecedencias
              q={q}
              passos={passos}
              disponiveis={disponiveis}
              naTela={naTela}
              ativa={prevista}
              desenhada={prevista}
              aoFaltar={(min) => setFaltou({ min, c: q?.c })}
              motivo={motivo}
            />
          )}
          {semEscolhida && <Aviso>{semEscolhida}</Aviso>}
          {numerosOk && q && (
            <Numeros
              cor={COR_SISTEMA}
              frase={fraseDoRastreio(q, noQuadro.rastreio, ultimo, instante)}
              titulo={
                <>
                  Imagem das {formatTimeBrt(q.t)}
                  <span className="text-ink-500">·</span>
                  <span className="min-w-0 truncate text-ink-400">GOES-19, canal 13</span>
                </>
              }
              numeros={[
                { rotulo: 'Sistemas', valor: formatInt(q.n) },
                { rotulo: 'Área total', valor: areaCurta(q.km2), unidade: 'km²' },
                { rotulo: 'Topo mais frio', valor: q.tmin !== null ? formatInt(q.tmin) : '—', unidade: 'K' },
              ]}
            />
          )}
          {(fraseNaCaixa || falarDaPrevisao || irAoUltimo) && (
          <div className="rounded-lg border border-ink-700/70 bg-ink-800/50 px-2.5 py-1.5 text-xs leading-snug text-ink-200" role="status">
            {q && fraseNaCaixa && <span className="mr-1 font-medium text-white tabular-nums">{formatTimeBrt(q.t)}</span>}
            {fraseNaCaixa && fraseDoRastreio(q, noQuadro.rastreio, ultimo, instante)}
            {falarDaPrevisao && (
              <span className={clsx(fraseNaCaixa && 'mt-1 block')} data-testid="situacao-da-previsao">
                {foraDaOrigem ? (
                  <>
                    A previsão parte da última imagem rastreada: nas anteriores, só a observação (e o sistema que você tocar).{' '}
                    <button
                      type="button"
                      className="font-semibold text-mata-300 underline focus-visible:outline-2 focus-visible:outline-mata-400"
                      onClick={() => verPrevisao(undefined, 'mais-nova')}
                    >
                      Ir à previsão
                    </button>
                  </>
                ) : (
                  fraseDaPrevisao(q, noQuadro.previsao, naTela, vazia)
                )}
                {noQuadro.previsao === 'falhou' && (
                  <>
                    {' '}
                    <button type="button" className="font-semibold text-mata-300 underline focus-visible:outline-2 focus-visible:outline-mata-400" onClick={refreshRastreio}>
                      Tentar de novo
                    </button>
                  </>
                )}
              </span>
            )}
            {irAoUltimo && (
              <button
                type="button"
                onClick={() => useTimeStore.getState().setIndex(indiceDoUltimo)}
                className="mt-1 flex items-center gap-1 font-semibold text-mata-300 hover:underline focus-visible:outline-2 focus-visible:outline-mata-400"
              >
                <History className="size-3.5" aria-hidden />
                Ir para o último rastreio ({formatTimeBrt(ultimo)})
              </button>
            )}
          </div>
          )}
          {rastreio && noQuadro.rastreio === 'ok' && <Sistemas itens={sistemas} onPick={props.onPick} />}
        </>
      )}

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-ink-700/60 pt-2 text-[11px] text-ink-300">
        <span className="flex items-center gap-1.5">
          <Traco traco={TRACO_SISTEMA} />
          sistema{indice?.limiares[0] !== undefined ? ` (${formatInt(indice.limiares[0])} K)` : ''}
        </span>
        {nucleos && (
          <span className="flex items-center gap-1.5">
            <Traco traco={TRACO_NUCLEO} />
            núcleo{indice?.limiares[1] !== undefined ? ` (${formatInt(indice.limiares[1])} K)` : ''}
          </span>
        )}
        <span className="flex items-center gap-1.5">
          <Traco traco={TRACO_PREVISTO} />
          previsto
        </span>
        {/* O cone do sistema tocado (trajetoria.ts): a mesma amostra da ficha. */}
        <span className="flex items-center gap-1.5">
          <span
            className="inline-block h-2.5 w-4 shrink-0 rounded-sm border border-dashed"
            style={{ borderColor: COR_PREVISAO, background: comAlfa(COR_PREVISAO, ALFA_DAS_FAIXAS_DO_CONE[0]) }}
            aria-hidden
          />
          caminho provável (ficha aberta)
        </span>
      </div>
    </div>
  );
}

const idDaAba = (k: AbaDoRastreio) => `aba-rastreio-${k}`;
const idDoPainel = (k: AbaDoRastreio) => `painel-rastreio-${k}`;

/** O que cada aba põe no mapa agora: para o ponto "No mapa" das abas e da aba lateral. */
function useNoMapaPorAba(): Record<AbaDoRastreio, boolean> {
  const nuvens = useTrackingStore((s) => s.rastreio || s.previsao);
  const chuva = useRastreioChuvaStore((s) => s.ligado);
  const satelite = useGoesStore((s) => s.layers.some((l) => l.visible));
  return { nuvens: nuvens && satelite, chuva: chuva && satelite && !!BASE_RASTREIO_CHUVA };
}

/**
 * O conteúdo do painel, igual no cartão do desktop e na folha do celular: as
 * abas (qual rastreio se está vendo; os dois podem estar no mapa ao mesmo
 * tempo, cada um com o seu interruptor) e o conteúdo da aba escolhida. Sem o
 * rastreio da chuva configurado (`VITE_RASTREIO_CHUVA_URL` vazio), só as nuvens, sem abas.
 */
/** A aba na tela: a escolhida, ou as nuvens sem o rastreio da chuva configurado. */
function useAba(): AbaDoRastreio {
  const escolhida = useTrackingStore((s) => s.aba);
  return BASE_RASTREIO_CHUVA ? escolhida : 'nuvens';
}

/**
 * O conteúdo da aba escolhida, igual no cartão do desktop e na folha do
 * celular. As abas ficam no cabeçalho (`Cabecalho`); sem o rastreio da chuva
 * configurado (`VITE_RASTREIO_CHUVA_URL` vazio), só as nuvens, sem abas.
 */
export function CorpoDoPainel(props: { onPick: (item: SistemaNaLista) => void; onPickChuva: (item: SistemaChuvaNaLista) => void }) {
  const aba = useAba();
  const comChuva = !!BASE_RASTREIO_CHUVA;
  return (
    <div
      className="p-3"
      role={comChuva ? 'tabpanel' : undefined}
      id={idDoPainel(aba)}
      aria-labelledby={comChuva ? idDaAba(aba) : undefined}
      data-painel-rastreio={aba}
    >
      {aba === 'nuvens' ? <TrackingBody onPick={props.onPick} /> : <PainelDaChuva onPick={props.onPickChuva} />}
    </div>
  );
}

/**
 * O cabeçalho: com os dois rastreios, as abas no lugar do título (que fica
 * para o leitor de tela) — a folha do celular não tem altura a perder; com um
 * só, o título, como antes.
 */
function Cabecalho(props: { onClose: () => void }) {
  const aba = useAba();
  const noMapa = useNoMapaPorAba();
  const fechar = <IconButton icon={X} label="Fechar rastreio e previsão" size="sm" onClick={props.onClose} className="shrink-0 pointer-coarse:size-11" />;
  if (!BASE_RASTREIO_CHUVA) {
    return (
      <header className="flex items-center gap-1.5 border-b border-ink-700/70 py-1.5 pr-1.5 pl-3">
        <IconeRastreio className="size-4 shrink-0 text-mata-400" aria-hidden />
        <h2 className="min-w-0 flex-1 truncate text-sm font-semibold text-ink-100">Rastreio e previsão</h2>
        {fechar}
      </header>
    );
  }
  return (
    <header className="flex items-center gap-1.5 border-b border-ink-700/70 py-1 pr-1.5 pl-2">
      <h2 className="sr-only">Rastreio e previsão</h2>
      <div className="min-w-0 flex-1">
        <Abas<AbaDoRastreio>
          atual={aba}
          aoEscolher={(k) => useTrackingStore.getState().setAba(k)}
          idDaAba={idDaAba}
          idDoPainel={idDoPainel}
          abas={[
            { chave: 'nuvens', nome: 'Nuvens', fonte: 'Nuvens frias do GOES-19, a cada 10 min', icone: Cloud, traco: TRACO_SISTEMA, noMapa: noMapa.nuvens },
            { chave: 'chuva', nome: 'Chuva', fonte: 'Chuva do GSMaP, a cada 30 min', icone: Droplets, traco: TRACO_CHUVA, noMapa: noMapa.chuva },
          ]}
        />
      </div>
      {fechar}
    </header>
  );
}

/**
 * O cartão do rastreio e o inspetor "Últimos focos" (ui/Inspector) flutuam no MESMO
 * lugar, sobre a borda direita do mapa: abrir um fecha o outro. Os dois lembrados
 * abertos de uma visita anterior: fica o dos focos.
 */
if (useTrackingStore.getState().painelAberto && useUiStore.getState().inspectorOpen) useTrackingStore.getState().setPainelAberto(false);
useTrackingStore.subscribe((a, b) => {
  if (a.painelAberto && !b.painelAberto && useUiStore.getState().inspectorOpen) useUiStore.getState().setInspector(false);
});
useUiStore.subscribe((a, b) => {
  if (a.inspectorOpen && !b.inspectorOpen && useTrackingStore.getState().painelAberto) useTrackingStore.getState().setPainelAberto(false);
});

/** Ponto na aba: verde com algum rastreio no mapa (nuvens ou chuva), apagado sem nenhum. */
function useNoMapa(): boolean {
  const { nuvens, chuva } = useNoMapaPorAba();
  return nuvens || chuva;
}

function Desktop(props: { map: OlMap }) {
  const aberto = useTrackingStore((s) => s.painelAberto);
  const { setPainelAberto } = useTrackingStore.getState();
  const ref = useRef<HTMLElement>(null);
  const primeiro = useRef(true);
  // Foco vai para o cartão quando ele abre por ação do usuário (não na carga).
  useEffect(() => {
    if (primeiro.current) {
      primeiro.current = false;
      return;
    }
    if (aberto) ref.current?.focus();
  }, [aberto]);

  // Fechado, a aba fica na pilha da direita (`AbaDoRastreio`, montada pelo App).
  if (!aberto) return null;
  return (
    <aside
      ref={ref}
      tabIndex={-1}
      aria-label="Rastreio e previsão"
      data-testid="tracking-panel"
      // À esquerda da coluna do zoom e da aba dos últimos focos, que continuam alcançáveis.
      className="absolute top-[60px] right-14 z-20 flex max-h-[calc(100%-120px)] w-max flex-col overflow-hidden rounded-xl border border-ink-600/90 bg-ink-900/95 shadow-2xl outline-none"
    >
      <Cabecalho onClose={() => setPainelAberto(false)} />
      <div className="min-h-0 flex-1 overflow-y-auto [scrollbar-gutter:stable]">
        {/* Largura do conteúdo fixa, com ou sem barra de rolagem: 360 px (era 340 até as abas de 03/11/2026,
            em que o nome da aba cortava), a mesma do cartão do celular deitado. */}
        <div className="w-[360px]">
          <CorpoDoPainel onPick={(item) => irAoSistema(props.map, item, false)} onPickChuva={(item) => irAChuva(props.map, item, false)} />
        </div>
      </div>
    </aside>
  );
}

function Celular(props: { map: OlMap }) {
  const aberto = useTrackingStore((s) => s.folhaAberta);
  const setAberto = useTrackingStore.getState().setFolhaAberta;
  const layerRef = useRef<HTMLDivElement>(null);
  const sheetRef = useRef<HTMLDivElement>(null);
  useModalLayer(aberto, { onEscape: () => setAberto(false), ref: layerRef });
  const fecharParaAFicha = () => {
    setAberto(false);
    // A folha abre pelos ajustes do Satélite (a folha das camadas, modal): fecha os dois,
    // senão a ficha nasce atrás deles e o mapa fica travado.
    useUiStore.getState().set({ sheetOpen: false, sheetStage: 'camadas' });
  };
  useEffect(() => {
    if (aberto) sheetRef.current?.focus();
  }, [aberto]);

  return (
    <>
      {/* O botão da previsão (que fazia as vezes do botão do rastreio) mora na linha do pé do
          celular (ui/RelogioDoPe). A folha continua abrindo pelo atalho na aba Satélite das camadas. */}
      {aberto &&
        // Portal para o <body> (fora de #root, que fica `inert` com a folha aberta), como a MobileSheet.
        createPortal(
          <div ref={layerRef}>
            <div className="fixed inset-0 z-40 bg-ink-950/30" onClick={() => setAberto(false)} aria-hidden />
            <div
              ref={sheetRef}
              role="dialog"
              aria-modal="true"
              aria-label="Rastreio e previsão"
              tabIndex={-1}
              data-testid="tracking-panel"
              className={clsx(
                'fixed z-50 flex flex-col border-ink-600 bg-ink-900 shadow-2xl outline-none',
                // Em pé: folha inferior de altura fixa — não cresce nem encolhe sob o dedo quando um
                // controle muda o conteúdo. 62% da tela (60% até as abas Nuvens/Chuva no cabeçalho,
                // 03/11/2026) para o cabeçalho da lista caber inteiro em 390×844; o piso de 25,5rem é para telas baixas (360×640), onde a porcentagem
                // cortaria o seletor de antecedências na borda.
                'portrait:inset-x-0 portrait:bottom-0 portrait:h-[max(62dvh,25.5rem)] portrait:max-h-[calc(100dvh-4rem)] portrait:rounded-t-2xl portrait:border-t',
                'portrait:pr-[env(safe-area-inset-right)] portrait:pb-[env(safe-area-inset-bottom)] portrait:pl-[env(safe-area-inset-left)]',
                // Deitado: cartão lateral à direita, como o do desktop. Uma folha inferior comeria a
                // altura, que é o que falta; de lado, o mapa e o aviso da previsão ficam à vista.
                'landscape:inset-y-0 landscape:right-0 landscape:w-[min(calc(360px+env(safe-area-inset-right)),64vw)] landscape:rounded-l-2xl landscape:border-l',
                'landscape:pt-[env(safe-area-inset-top)] landscape:pr-[env(safe-area-inset-right)] landscape:pb-[env(safe-area-inset-bottom)]',
              )}
            >
              <Cabecalho onClose={() => setAberto(false)} />
              <div className="min-h-0 flex-1 overflow-y-auto [scrollbar-gutter:stable]">
                <CorpoDoPainel
                  onPick={(item) => {
                    fecharParaAFicha();
                    irAoSistema(props.map, item, true);
                  }}
                  onPickChuva={(item) => {
                    fecharParaAFicha();
                    irAChuva(props.map, item, true);
                  }}
                />
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}

/** Desktop: a aba do rastreio, na pilha da direita (App `AbasDaDireita`), com o cartão fechado. */
export function AbaDoRastreio() {
  const aberto = useTrackingStore((s) => s.painelAberto);
  const noMapa = useNoMapa();
  if (!BASE_RASTREIO || aberto) return null;
  return (
    <button
      type="button"
      onClick={() => useTrackingStore.getState().setPainelAberto(true)}
      aria-label="Abrir rastreio e previsão"
      aria-expanded={false}
      data-tracking-tab
      className="flex h-40 min-h-28 w-7 shrink items-center justify-center rounded-l-xl border border-r-0 border-ink-600/90 bg-ink-900/95 text-ink-100 shadow-lg hover:bg-ink-800 focus-visible:outline-2 focus-visible:outline-mata-400"
    >
      <span className="flex rotate-180 items-center gap-1.5 text-[11px] font-medium whitespace-nowrap [writing-mode:vertical-rl]">
        <IconeRastreio className="size-3.5 rotate-90 text-mata-400" aria-hidden />
        Rastreio e previsão
        <span className={clsx('size-1.5 rounded-full', noMapa ? 'bg-mata-400' : 'bg-ink-500')} aria-hidden />
      </span>
    </button>
  );
}

/** Montado pelo App sobre o mapa (dentro do <main>). */
export function TrackingFloat(props: { mobile: boolean }) {
  const map = useMapInstance((s) => s.map);
  if (!map || !BASE_RASTREIO) return null;
  return props.mobile ? <Celular map={map} /> : <Desktop map={map} />;
}

/**
 * Aviso sobre o mapa enquanto há polígono previsto na tela: quem olha só o
 * mapa precisa saber que o tracejado não é observação. Some na observação
 * (antecedência 0) e quando o quadro não tem previsão.
 *
 * Junto, uma moldura ciano em volta do mapa (só na tela: é DOM, a exportação
 * não a leva) e o botão "Agora", que sai da previsão para a imagem mais
 * recente. O mapa não é esmaecido: a imagem do IR é escala de temperatura.
 */
export function ForecastChip(props: { mobile: boolean }) {
  // Só com contorno previsto desenhado: arquivo vazio, ou sem esta antecedência, não tem o que anunciar.
  const naTela = usePrevisaoDesenhada();
  const instante = useTimeStore((s) => shownTimeOf(s));
  // Celular deitado com o painel aberto: o aviso vai para o meio do mapa que sobra à esquerda do cartão.
  const aoLadoDoCartao = useTrackingStore((s) => s.folhaAberta) && props.mobile;
  // Desktop com o painel lateral aberto: o aviso fica no meio da parte do mapa que ele não cobre.
  const painel = usePainelLateralAberto() && !props.mobile ? SIDEBAR_WIDTH : 0;
  // Previsão desligada e um só sistema projetado pela ficha: o aviso diz qual, sem a moldura (o resto do mapa é observação).
  const satelite = useGoesStore((s) => s.layers.some((l) => l.visible));
  const doSelecionado = useTrackingStore((s) => previsaoDoSelecionado(s, satelite));
  const uid = useTrackingStore((s) => s.selecao?.uid ?? null);
  const so = naTela <= 0 && doSelecionado > 0 ? doSelecionado : 0;
  if (!BASE_RASTREIO || (naTela <= 0 && so <= 0) || instante === null) return null;
  const h = naTela > 0 ? naTela : so;
  return (
    <>
      {naTela > 0 && <div
        data-moldura-previsao
        className="pointer-events-none absolute inset-0 z-[5]"
        style={{ boxShadow: `inset 0 0 0 2px ${comAlfa(COR_PREVISAO, 0.6)}` }}
        aria-hidden
      />}
      <div
        role="status"
        data-forecast-chip
        className={clsx(
          'pointer-events-none absolute left-1/2 z-10 flex -translate-x-1/2 items-center gap-1.5 rounded-full border border-ink-600/90 bg-ink-900/95 py-0.5 pr-0.5 pl-3 text-xs font-semibold whitespace-nowrap text-white shadow-lg tabular-nums',
          aoLadoDoCartao && 'landscape:left-[calc((100%-min(360px+env(safe-area-inset-right),64vw))/2)]',
        )}
        // Abaixo da linha de chips do topo (36 px no desktop, 44 px no celular), para não cobrir nenhum.
        style={{
          top: props.mobile ? 'calc(env(safe-area-inset-top) + 12px + 44px + 8px)' : '56px',
          ...(painel ? { left: `calc(50% + ${painel / 2}px)` } : {}),
        }}
      >
        <Traco traco={TRACO_PREVISTO} />
        <span>
          {so > 0 && uid !== null ? `Sistema ${uid} · previsão` : 'Previsão'} {rotuloAntecedencia(h, { unidade: true })} · {formatTimeBrt(instante + h * MINUTE_MS)}
          {!props.mobile && <span className="font-normal text-ink-300"> · imagem das {formatTimeBrt(instante)}</span>}
        </span>
        <button
          type="button"
          onClick={() => (so > 0 ? useTrackingStore.getState().setAntecedenciaDaSelecao(null) : sairDaPrevisao('ultimo'))}
          title={so > 0 ? 'Tirar a previsão deste sistema do mapa' : 'Sair da previsão e ir à imagem mais recente'}
          // O desenho tem 24 px; a área de toque, 44 (o ::after passa da borda, sem crescer o aviso).
          className="pointer-events-auto relative inline-flex h-6 items-center rounded-full border border-ink-500 bg-ink-800 px-2.5 text-[11px] font-semibold text-ink-100 hover:bg-ink-700 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-mata-400 pointer-coarse:after:absolute pointer-coarse:after:-inset-x-1 pointer-coarse:after:-inset-y-2.5 pointer-coarse:after:content-['']"
        >
          Agora
        </button>
      </div>
    </>
  );
}

/**
 * Assinatura do algoritmo, no canto inferior direito do mapa (onde ficava o
 * "i" dos créditos, que foram para o "Sobre"; no celular, no esquerdo): leva
 * ao repositório do pyForTraCC. A largura vai para `--amaview-marca`, que o
 * crédito da foto de fundo (index.css) usa para ficar ao lado dela no desktop; a
 * altura, para `--amaview-marca-altura`, que no celular o põe acima dela.
 */
export function PyFortraccMark(props: { mobile: boolean }) {
  const ref = useRef<HTMLAnchorElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const raiz = document.documentElement;
    const medir = () => {
      const r = el.getBoundingClientRect();
      raiz.style.setProperty('--amaview-marca', `${Math.ceil(r.width)}px`);
      // A altura: no celular o crédito da foto de fundo fica logo acima da assinatura (index.css).
      raiz.style.setProperty('--amaview-marca-altura', `${Math.ceil(r.height)}px`);
    };
    medir();
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(medir);
    ro?.observe(el);
    return () => {
      ro?.disconnect();
      raiz.style.removeProperty('--amaview-marca');
      raiz.style.removeProperty('--amaview-marca-altura');
    };
  }, []);
  return (
    <a
      ref={ref}
      href={PYFORTRACC_URL}
      target="_blank"
      rel="noopener noreferrer"
      data-pyfortracc-mark
      title="pyForTraCC: o algoritmo de rastreio e previsão (abre o repositório)"
      className={clsx(
        'glass absolute bottom-2 z-10 flex rounded-lg py-1 leading-none no-underline shadow-lg transition-colors hover:bg-ink-800',
        'focus-visible:outline-2 focus-visible:outline-mata-400',
        // No celular, no canto inferior ESQUERDO (o direito ficou para a legenda recolhida e a previsão;
        // 08/10/2026), em duas linhas: estreita o bastante para não encostar no play, no meio do pé.
        // Em pé o trilho de camadas termina acima dela; deitado, ela fica à direita do trilho.
        props.mobile
          ? 'left-[calc(8px+env(safe-area-inset-left))] flex-col items-start gap-0.5 px-1.5 deitado:left-[var(--trilho-fim)]'
          : 'right-2 items-baseline gap-1 px-2',
      )}
    >
      <span className={clsx('text-[10px] font-medium text-ink-200')}>
        Tracking &amp; forecast
      </span>{' '}
      <span className={clsx('text-[10px] text-ink-400', props.mobile && 'hidden')}>by</span>{' '}
      <span
        className="text-[13px] font-semibold"
        style={{ color: COR_SISTEMA }}
      >
        {props.mobile && <span className="mr-1 text-[10px] font-normal text-ink-400">by </span>}
        PyFortraCC
      </span>
    </a>
  );
}
